// Catálogo único de secciones navegables del backoffice.
// Fuente de verdad para: (1) el grid de acceso rápido del Home y
// (2) la resolución de "accesos recientes" (recentSections.js).
// Cada entrada es una ruta de SECCIÓN ya existente en AppRouter — sin registros
// individuales ni PII. Mantener en sync con AppRouter.js / sibebarPaths.js.

import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import HandshakeIcon from "@mui/icons-material/Handshake";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import CreditCardOffIcon from "@mui/icons-material/CreditCardOff";
import HubIcon from "@mui/icons-material/Hub";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";

import { roles } from "./roles";

// path → { title, description, icon }. El `path` es el pathname canónico
// (sin query string); la resolución normaliza descartando `?...`.
export const SECTIONS = {
  "/personas/socios": { title: "Socios", description: "Alta, modificación y bajas", icon: <PeopleAltIcon /> },
  "/personas/bajas": { title: "Bajas de socios", description: "Gestión de bajas", icon: <PersonRemoveIcon /> },
  "/profesional/estado-cola": { title: "Profesionales", description: "Registro y estado de cola", icon: <AccountBoxIcon /> },
  "/profesional/active-log": { title: "Profesionales en línea", description: "Actividad de profesionales", icon: <WifiTetheringIcon /> },
  "/empresas/listado": { title: "Empresas", description: "Financiadoras y convenios", icon: <BusinessCenterIcon /> },
  "/empresas/convenios": { title: "Convenios", description: "Convenios con empresas", icon: <HandshakeIcon /> },
  "/configuracion/planes": { title: "Planes", description: "Configuración de planes", icon: <BuildCircleIcon /> },
  "/configuracion/email-blacklist": { title: "Lista Negra Email", description: "Emails bloqueados", icon: <MarkEmailUnreadIcon /> },
  "/configuracion/bin-blacklist": { title: "Lista Negra BIN", description: "BINes bloqueados", icon: <CreditCardOffIcon /> },
  "/configuracion/channels-dashboard": { title: "Gestión de Canales", description: "Canales de venta", icon: <HubIcon /> },
  "/configuracion/validadores": { title: "Validador Tarjeta/CBU", description: "Validación de medios de pago", icon: <CreditScoreIcon /> },
  "/obra-social/socios": { title: "Obra Social", description: "Socios de obra social", icon: <HealthAndSafetyIcon /> },
  "/notificaciones": { title: "Notificaciones", description: "Avisos a socios", icon: <NotificationsIcon /> },
  "/reportes": { title: "Reportes", description: "Indicadores de gestión", icon: <AssessmentIcon /> },
  "/canales/tmotos": { title: "Software de Venta", description: "TMotos y otros canales", icon: <TrendingUpIcon /> },
  "/canales/voyenbus": { title: "Voy en Bus", description: "Canal Voy en Bus", icon: <TrendingUpIcon /> },
  "/canales/gallion": { title: "Gallion", description: "Canal Gallion", icon: <TrendingUpIcon /> },
  "/nuevo-cliente": { title: "Nuevo cliente", description: "Cargar una venta", icon: <BusinessCenterIcon /> },
  "/mis-ventas": { title: "Mis ventas", description: "Ver mis ventas cargadas", icon: <MoveToInboxIcon /> },
};

// Paths accesibles por rol → filtra qué recientes mostrar y qué grid estático
// armar, para no ofrecer secciones que el rol no puede abrir.
export const SECTION_PATHS_BY_ROLE = {
  [roles.ROLE_OPERATOR]: [
    "/personas/socios",
    "/personas/bajas",
    "/profesional/estado-cola",
    "/profesional/active-log",
    "/empresas/listado",
    "/empresas/convenios",
    "/configuracion/planes",
    "/configuracion/email-blacklist",
    "/configuracion/bin-blacklist",
    "/configuracion/channels-dashboard",
    "/configuracion/validadores",
    "/obra-social/socios",
    "/notificaciones",
    "/reportes",
    "/canales/tmotos",
    "/canales/voyenbus",
    "/canales/gallion",
  ],
  [roles.ROLE_SELLER]: ["/nuevo-cliente", "/mis-ventas"],
};

// Selección curada que se muestra como grid de acceso rápido por defecto
// (cuando todavía no hay historial reciente).
export const FEATURED_PATHS_BY_ROLE = {
  [roles.ROLE_OPERATOR]: [
    "/personas/socios",
    "/profesional/estado-cola",
    "/empresas/listado",
    "/configuracion/planes",
    "/reportes",
    "/canales/tmotos",
  ],
  [roles.ROLE_SELLER]: ["/nuevo-cliente", "/mis-ventas"],
};

// Quita query string / hash y devuelve el pathname canónico.
const normalizePath = (path) => (path || "").split("?")[0].split("#")[0];

// Resuelve un path a su sección del catálogo. Si se pasa `role`, solo resuelve
// cuando ese rol tiene acceso (devuelve null en caso contrario).
export const getSectionByPath = (path, role) => {
  const pathname = normalizePath(path);
  const section = SECTIONS[pathname];
  if (!section) return null;
  if (role && !SECTION_PATHS_BY_ROLE[role]?.includes(pathname)) return null;
  return { path: pathname, ...section };
};
